// iter137 → iter151 — PWA install banner. Suppressed until the user has
// made at least one successful save OR is on their 2nd visit, and never
// on /login or /register. Dismissal persists 30 days.
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X } from "lucide-react";

const KEY_DISMISS = "mdm.pwaInstallDismissedAt";
const KEY_FIRST_SAVE = "mdm.firstSaveDone";
const KEY_VISIT_COUNT = "mdm.visitCount";
const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;   // 30 days
const PUBLIC_PATHS = ["/login", "/register", "/about", "/contact", "/privacy", "/terms", "/refund"];

function recentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(KEY_DISMISS) || "0", 10);
    return Number.isFinite(ts) && ts > 0 && Date.now() - ts < COOLDOWN_MS;
  } catch (e) { return false; }
}

function userReady() {
  // Gate per spec: show the banner only when BOTH of these are true:
  //   • not on login / register (handled by caller),
  //   • AND (first save has happened OR visit count ≥ 2).
  try {
    const firstSave = !!localStorage.getItem(KEY_FIRST_SAVE);
    const visits = parseInt(localStorage.getItem(KEY_VISIT_COUNT) || "0", 10) || 0;
    return firstSave || visits >= 2;
  } catch { return false; }
}

export default function PWAInstallBanner() {
  const [evt, setEvt] = useState(null);
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  const isPublic = PUBLIC_PATHS.includes(location.pathname) || location.pathname.startsWith("/setup-form/");

  useEffect(() => {
    if (isPublic) return undefined;
    if (recentlyDismissed()) return undefined;
    // Already installed → standalone display-mode → suppress.
    if (typeof window !== "undefined" && window.matchMedia &&
        window.matchMedia("(display-mode: standalone)").matches) return undefined;
    const onPrompt = (e) => {
      e.preventDefault();
      setEvt(e);
      if (userReady()) {
        // Slight delay so the banner doesn't compete with login toasts.
        setTimeout(() => setVisible(true), 1200);
      }
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);
    // In case the prompt fired BEFORE user was ready and later becomes
    // ready, re-check on storage events (DailyEntry stamps KEY_FIRST_SAVE
    // on save success).
    const onStorage = () => {
      if (evt && userReady() && !isPublic && !recentlyDismissed()) {
        setVisible(true);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("storage", onStorage);
    };
  }, [isPublic, evt]);

  if (isPublic || !visible || !evt) return null;

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(KEY_DISMISS, String(Date.now())); } catch (e) {}
  };
  const install = async () => {
    try {
      evt.prompt();
      await evt.userChoice;
    } catch (e) { /* user cancelled */ }
    dismiss();
  };

  return (
    <div
      role="dialog"
      aria-label="Install MDM Sahayak"
      data-testid="pwa-install-banner"
      className="fixed inset-x-3 z-40 flex items-center gap-3 rounded-2xl border border-brand-200 bg-white p-3 shadow-[0_18px_45px_-15px_rgba(37,99,235,0.45)] md:hidden"
      style={{ bottom: "calc(80px + env(safe-area-inset-bottom, 0px))" }}
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-600 text-white shadow-inner">
        <Download className="h-5 w-5" strokeWidth={2.25} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-slate-900">Install MDM Sahayak</p>
        <p className="text-[11px] leading-tight text-slate-600">
          Add to your home screen for one-tap access and an app-like fullscreen feel.
        </p>
      </div>
      <button
        type="button"
        onClick={install}
        data-testid="pwa-install-confirm"
        className="rounded-lg bg-brand-600 px-3 py-2 text-xs font-bold text-white shadow active:scale-95"
      >
        Install
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="pwa-install-dismiss"
        className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
